import React from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Skeleton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import SpeedIcon from "@mui/icons-material/Speed";

import { MetricCard } from "../shared";

/**
 * Panel de salud del sistema para Admin Dashboard
 */
export default function SystemHealthPanel({ loading, health, alerts }) {
  const getStatusIcon = (status) => {
    switch (status) {
      case "healthy":
        return <CheckCircleIcon color="success" fontSize="small" />;
      case "degraded":
        return <WarningIcon color="warning" fontSize="small" />;
      case "down":
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <CheckCircleIcon color="disabled" fontSize="small" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "success";
      case "degraded":
        return "warning";
      case "down":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={150} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6" fontWeight={600}>
          System Health
        </Typography>

        {/* Overall Status */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
          }}
        >
          <MetricCard
            title="Response Time"
            value={`${health.responseTime}ms`}
            trend={health.responseTimeTrend}
            trendLabel="vs last hour"
            trendInverted={true}
            icon={<SpeedIcon />}
            color="primary"
          />

          <MetricCard
            title="Error Rate"
            value={`${health.errorRate}%`}
            trend={health.errorRateTrend}
            trendLabel="vs last hour"
            trendInverted={true}
            color={health.errorRate > 1 ? "error" : "success"}
          />
        </Box>

        {/* Uptime Badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            System Uptime:
          </Typography>
          <Chip
            label={`${health.uptime}%`}
            color="success"
            size="small"
          />
        </Box>

        {/* Services Status */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Services
          </Typography>
          <List dense disablePadding>
            {health.services?.map((service) => (
              <ListItem key={service.name} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getStatusIcon(service.status)}
                </ListItemIcon>
                <ListItemText
                  primary={service.name}
                  secondary={`${service.latency}ms`}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                <Chip
                  label={service.status}
                  size="small"
                  color={getStatusColor(service.status)}
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Security Alerts */}
        {alerts?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Alerts
            </Typography>
            <Stack spacing={1}>
              {alerts.map((alert, index) => (
                <Alert
                  key={index}
                  severity={alert.severity}
                  sx={{ py: 0.5 }}
                >
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
